import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useLocation, useParams, useNavigate, Outlet } from 'react-router-dom'
import type { Artist, ArtistId } from 'types/artist'
import type { State } from 'types/redux'
import { loadArtist } from '@disco/state-management/actions/artist-actions'
import { SHOWCASES } from '@disco/constants/FeatureTypes'
import { getArtist } from '@disco/state-management/selectors/artists'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { useComposerInsteadOfArtist } from '@disco/utils/composerInsteadOfArtist'
import DocumentHeadTags from 'components/document-head-tags'
import { TabNav } from 'components/ui'
import Heading from 'components/ui/heading'
import ModalWindow from 'components/ui/modal-window'
import Skeleton from 'components/ui/skeleton'

export default (): React.ReactNode => {
  const location = useLocation()
  const params = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const artistId: ArtistId = parseInt(params.artistId, 10)
  const showcasesEnabled: boolean = useFeatureEnabled(SHOWCASES)
  const artist: Artist = useSelector((state: State) =>
    getArtist(state, artistId),
  )
  const typeData = useComposerInsteadOfArtist().value
  useEffect(() => {
    dispatch(loadArtist(artistId))
  }, [])

  const handleCancel = () => {
    const returnTo = '/'
    navigate(location.state?.returnTo ?? returnTo)
  }

  const NAV_ITEMS = useMemo(() => {
    const base = '/explorer/artists'
    let items = [
      {
        label: 'Profile',
        link: {
          pathname: `${base}/${artistId}/profile`,
          state: location.state,
        },
      },
      {
        label: `Linked ${typeData}s`,
        link: {
          pathname: `${base}/${artistId}/linked-artists`,
          state: location.state,
        },
      },
    ]

    if (showcasesEnabled) {
      items = [
        ...items,
        {
          label: 'Pages',
          link: {
            pathname: `${base}/${artistId}/pages`,
            state: location.state,
          },
        },
      ]
    }

    return items
  }, [artistId, showcasesEnabled])
  return (
    <ModalWindow
      width="medium"
      header={
        <Heading size={700}>
          {artist && !artist.loading ? (
            `Manage ${typeData} – ${artist.name}`
          ) : (
            <Skeleton />
          )}
        </Heading>
      }
      isOpen={true}
      onClose={handleCancel}
    >
      <DocumentHeadTags title={`Manage ${typeData} | DISCO`} />
      <TabNav className="Tabs--modal" items={NAV_ITEMS} />
      <Outlet />
    </ModalWindow>
  )
}
